/* YSL gift-with-purchase — auto-adds the free item for a Buy X Get Y discount.

   Shopify's native BXGY discounts the gift only when it is ALREADY in the cart;
   it never adds it. This watches the cart and puts the gift line in (and takes
   it back out when the buy condition stops being met), so the customer sees the
   0₫ line without hunting for the product.

   Rules come from the gwp_rule metaobject (see snippets/ysl-gwp.liquid).
   They must mirror the discount set up in Admin — the storefront can't read
   discount config, so nothing here enforces the price. The DISCOUNT does that;
   this only manages cart contents. A rule that drifts from its discount means a
   gift line that isn't free, so keep the two in sync.

   The gift line carries the private property _gwp:<rule id>; every cart surface
   already filters `_`-prefixed keys, so it stays hidden from the customer.

   The gift is locked: the cart and drawer render it with no stepper and no
   remove control, and this script puts back any quantity change or deletion
   that reaches the Ajax API anyway. It leaves only when the product that earned
   it does. See docs/wiki/gift-wrapping-build.md for the sibling _giftwrap flow. */
(function () {
  'use strict';

  // Copy comes from window.yslI18n (rendered by snippets/ysl-i18n-js.liquid) because a
  // .js asset cannot run Liquid's `t` filter. The literal stays as the fallback, so a
  // missing key degrades to the Vietnamese copy instead of to an empty string.
  function T(k, fallback) { var d = window.yslI18n; return (d && d[k]) || fallback; }

  var RULES = window.YSL_GWP_RULES || [];
  /* An EMPTY rule list is not a reason to stop. It means every campaign was
     switched off while gift lines may still sit in carts — parked at qty 1 even
     for carts that never reached a threshold (see plan()). With no rule to own
     them they are all orphans: plan() drops them on the load-time sync, and the
     checkout handler drops them too. Bailing out here used to let them ride
     through to checkout as free goods. */
  if (!Array.isArray(RULES)) RULES = [];

  /* Calls queue instead of running concurrently: two syncs racing would each
     plan against a cart the other is about to change. Dropping the second call
     is not an option either — the cart it was reacting to would go
     unreconciled until the next event. */
  var queue = Promise.resolve(false);

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function ruleId(rule, i) { return String(rule.id || 'gwp' + i); }

  function num(v) { return parseInt(v, 10) || 0; }

  function isGift(item) { return !!(item.properties && item.properties._gwp); }
  function ruleById(id) {
    for (var i = 0; i < RULES.length; i++) if (ruleId(RULES[i], i) === id) return RULES[i];
    return null;
  }
  /* Does the cart hold anything the customer actually bought? Gifts, gift-wrap
     and loyalty tier gifts (_tier_gift) don't count — a cart of only those is
     "empty", and its dormant gift lines should be pulled so the empty state can
     show. */
  function hasReal(cart) {
    return cart.items.some(function (item) {
      var p = item.properties || {};
      return !p._gwp && !p._giftwrap && !p._tier_gift;
    });
  }

  /* Qty of prerequisite items in the cart. NO gift line counts towards any
     rule: letting one promotion's freebie qualify another chains gifts, and
     each sync would then change the cart the next one reads. */
  function buyQty(cart, rule) {
    var variants = (rule.buy_variants || []).map(String);
    var products = (rule.buy_products || []).map(String);
    var total = 0;
    cart.items.forEach(function (item) {
      if (isGift(item)) return;
      var hit = variants.indexOf(String(item.variant_id)) !== -1 ||
        products.indexOf(String(item.product_id)) !== -1;
      if (hit) total += item.quantity;
    });
    return total;
  }

  /* The order total without a rule's excluded products. final_line_price is net
     of line discounts but not of an order discount, so that one is shared out
     across the lines. */
  function spendExcluding(cart, ids) {
    var set = ids.map(String), out = 0, all = 0;
    cart.items.forEach(function (item) {
      var line = num(item.final_line_price);
      all += line;
      if (set.indexOf(String(item.product_id)) !== -1) out += line;
    });
    return all > 0 ? Math.max(0, cart.total_price - Math.round(out * Math.min(1, cart.total_price / all))) : 0;
  }

  /* How many gift units the cart should hold for a rule.

     Two rule shapes:
     - PRODUCT rule (buy_products / buy_variants): scales with the cart — buy 3
       of a 1-for-1 rule and 3 gifts come in.
     - ORDER-VALUE rule (min_subtotal, in the shop currency's major unit e.g.
       VND): the whole order must reach a spend threshold — one gift set when
       met, none below it. Mirrors a "minimum purchase amount" Buy X Get Y.
       exclude_products don't count towards the threshold.

     max_gifts caps the total and MUST mirror the discount's "maximum uses per
     order" — Shopify stops discounting past its own limit, so a rule that scales
     further just adds gifts the customer gets charged for. Leave max_gifts out
     (or 0) only when the discount itself is uncapped. */
  function wanted(cart, rule) {
    var per = num(rule.gift_qty) || 1;
    var max = num(rule.max_gifts);
    var want;
    if (rule.min_subtotal) {
      /* rule threshold is in the major unit; cart totals are in cents (×100).
         Scales in steps of the threshold: n× the amount → n gift sets (đơn 4M →
         1, 8M → 2 …). The gift is 0₫ so it can't push the cart across a step.
         max_gifts still caps it; leave it 0 for the uncapped ladder. */
      var min = num(rule.min_subtotal) * 100;
      var spent = rule.exclude_products ? spendExcluding(cart, rule.exclude_products) : cart.total_price;
      want = min > 0 ? Math.floor(spent / min) * per : 0;
    } else {
      var need = num(rule.buy_qty) || 1;
      want = Math.floor(buyQty(cart, rule) / need) * per;
    }
    return max > 0 ? Math.min(want, max) : want;
  }

  /* Gift writes go out on a fetch Shopify's web-pixel manager doesn't watch.
     It wraps the PAGE's window.fetch and reports every /cart/add.js, and every
     /cart/update.js that raises a quantity, as product_added_to_cart, so each
     0₫ gift reached GA4 (Google & YouTube app) and Klaviyo as an add_to_cart
     the customer never made. A blank same-origin iframe has its own, unwrapped
     fetch that still sends the cart cookie (measured 10/2026: normal fetch →
     add_to_cart:0, iframe fetch → nothing, for add.js and update.js alike).
     Falls back to the page fetch if the iframe can't be made. */
  var quietFrame = null;
  function quietFetch(url, opts) {
    try {
      if (!quietFrame || !quietFrame.isConnected) {
        quietFrame = document.createElement('iframe');
        quietFrame.setAttribute('aria-hidden', 'true');
        quietFrame.tabIndex = -1;
        quietFrame.style.display = 'none';
        document.body.appendChild(quietFrame);
      }
      var f = quietFrame.contentWindow && quietFrame.contentWindow.fetch;
      if (f) return f.call(quietFrame.contentWindow, new URL(url, window.location.href).href, opts);
    } catch (err) {}
    return fetch(url, opts);
  }

  function post(path, body) {
    return quietFetch(routesRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); });
  }

  /* Work out the whole cart in one pass: a quantity per existing line, plus how
     many gift units are still missing.

     It has to be one pass because line item keys are NOT stable — every cart
     mutation reissues them, so a second key-addressed call in the same sync
     silently targets a line that no longer exists. /cart/update.js takes an
     array positional to the current cart and applies it atomically.

     A rule's gift can legitimately occupy SEVERAL lines: Shopify splits a line
     by discount allocation, so once the discount's per-order limit is reached
     the free units and the charged ones sit on separate lines. Only the TOTAL
     is compared — collapsing them into one line makes Shopify split it straight
     back, and the two fight forever. */
  function plan(cart) {
    var updates = cart.items.map(function (item) { return item.quantity; });
    var adds = [];
    var touched = false;
    /* Keep the gift line in the cart ALWAYS while the cart has real items —
       dormant at qty 1 when the rule isn't met (hidden by CSS), scaled up when
       it is. So crossing the threshold is just an un-hide + a folded qty bump,
       never a fresh add.js round trip. Pulled only when no real item is left
       (so the empty state can show) or when it points at a stale variant. */
    var real = hasReal(cart);

    /* A gift line whose rule no longer exists belongs to nobody: the loop below
       only ever touches lines matching a CURRENT rule, so without this it would
       sit in the cart untouched. Liquid has no rule to evaluate it against
       either, so it renders as earned -- the customer sees a free gift that the
       checkout handler then strips out from under them. Drop it here instead,
       which is the same verdict checkout reaches, just earlier. */
    cart.items.forEach(function (item, idx) {
      if (!isGift(item)) return;
      if (ruleById(item.properties._gwp)) return;
      if (updates[idx] !== 0) { updates[idx] = 0; touched = true; }
    });

    RULES.forEach(function (rule, i) {
      if (!rule.gift_variant) return;
      var id = ruleId(rule, i);
      /* `wanted` = the EARNED units (0 when unmet); the line still holds ≥1 so it
         can reveal instantly, so the target is max(1, earned) while real items
         exist. paint()/Liquid hide it whenever earned is 0. */
      var earned = wanted(cart, rule);
      var want = real ? Math.max(1, earned) : 0;

      var mine = [];
      var total = 0;
      cart.items.forEach(function (item, idx) {
        if (!item.properties || item.properties._gwp !== id) return;
        if (String(item.variant_id) !== String(rule.gift_variant)) {
          /* the rule now points at a different gift — drop the stale line */
          if (updates[idx] !== 0) { updates[idx] = 0; touched = true; }
          return;
        }
        mine.push(idx);
        total += item.quantity;
      });

      if (total === want) return;

      if (total > want) {
        /* Trim from the last line back, so the free units (allocated first)
           are the ones that survive. */
        var over = total - want;
        for (var k = mine.length - 1; k >= 0 && over > 0; k--) {
          var idx2 = mine[k];
          var take = Math.min(over, updates[idx2]);
          updates[idx2] -= take;
          over -= take;
          touched = true;
        }
        return;
      }

      /* total < want. If the gift line already exists (it does whenever the cart
         has real items — it stays dormant at ≥1), bump an existing line's qty via
         an UPDATE so the change can FOLD into the caller's own change.js (one
         round trip). Only a truly missing line needs add.js — which can't fold,
         and is the sole endpoint that sets the _gwp property. */
      if (mine.length) {
        updates[mine[0]] += (want - total);
        touched = true;
      } else {
        adds.push({ id: rule.gift_variant, quantity: want - total, properties: { _gwp: id } });
      }
    });

    return { updates: updates, adds: adds, changed: touched || adds.length > 0 };
  }

  /* EVERY cart surface in the DOM, not just the cart page. `sections` takes a
     comma-separated list, so one mutation can carry the re-rendered HTML for all
     of them — no separate ?sections round trip, and no cross-surface refresh
     afterwards. That matters most in the drawer: it used to get nothing back
     from gwp's own write and had to fetch its own render (a whole ~400ms).
     The drawer section is rendered globally by layout/theme.liquid, so on the
     cart page BOTH surfaces are present and both get rendered here. */
  function cartSurfaces() {
    var out = [];
    var cart = document.querySelector('.ysl-cart[data-section-id]');
    if (cart && window.YSLCart && window.YSLCart.applySection) {
      out.push({ id: cart.getAttribute('data-section-id'), apply: window.YSLCart.applySection });
    }
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && window.YSLCartDrawer && window.YSLCartDrawer.applySection) {
      /* applySection = patch-in-place when the structure allows, full render
         otherwise — same door the drawer's own mutations use. */
      out.push({ id: drawer.getAttribute('data-section-id') || 'ysl-cart-drawer', apply: window.YSLCartDrawer.applySection });
    }
    return out;
  }

  /* ---------- optimistic gift row ----------
     plan() already knows a gift is coming, but the add is a ~400ms round trip
     away. Paint a placeholder into each surface so the row appears WITH the
     click and the list doesn't sit there looking unchanged.

     Deliberately generic: the storefront has no title or image for a variant
     that is not in the cart yet (a variant id can't be resolved to a product in
     Liquid or the Ajax API), and inventing them would be worse than saying
     plainly that a gift is on its way. */
  var GHOST = 'ysl-gwp-ghost';

  function ghostShow(n) {
    var lists = document.querySelectorAll('.ysl-cd__list, .ysl-cart__list');
    lists.forEach(function (list) {
      for (var i = 0; i < n; i++) {
        var li = document.createElement('li');
        li.className = GHOST;
        li.setAttribute('aria-live', 'polite');
        li.innerHTML = '<span class="' + GHOST + '__img"></span>' +
          '<span class="' + GHOST + '__txt">' + T('cartAddingGift', 'Đang thêm quà tặng…') +
            '<em>' + T('cartFree', 'Miễn phí') + '</em></span>';
        list.appendChild(li);
      }
    });
  }

  function ghostClear() {
    document.querySelectorAll('.' + GHOST).forEach(function (el) { el.remove(); });
  }

  /* ALWAYS fetch the cart fresh at the START of the run — never plan off a
     snapshot handed in by the caller. Syncs are serialized through `queue`, but
     the caller's cart JSON is captured at DISPATCH time: when two edits land
     close together, the second run's snapshot predates the first run's gift/
     sample adds, so planning off it re-adds them and the gift quantity doubles.
     A fetch here reads the cart AFTER any earlier queued run mutated it, so each
     plan sees the true current state. One cart.js per sync is the cost of that
     correctness — cheaper than a doubled gift.
     Resolves: 'applied' (changed + re-rendered here), true (changed, caller must
     render), or false (nothing to do). */
  function run() {
    var cartP = fetch(routesRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });

    return cartP.then(function (cart) {
      var p = plan(cart);
      if (!p.changed) return false;

      var surfs = cartSurfaces();
      var ids = surfs.map(function (s) { return s.id; }).join(',');
      var done = false;
      var lastSections = null;

      /* No optimistic placeholder any more: the gift line is always present
         (dormant, hidden), so "earning" it is an un-hide via paint(), not an add
         — an "Đang thêm quà tặng…" ghost would only flash on the one-time dormant
         pre-add of a gift the customer hasn't earned. */

      function stamp(res) {
        if (!res.ok) return;
        done = true;
        if (res.body && res.body.sections) lastSections = res.body.sections;
      }

      var step = Promise.resolve();
      var needsUpdate = p.updates.some(function (q, i) { return q !== cart.items[i].quantity; });
      if (needsUpdate) {
        var ubody = { updates: p.updates };
        if (ids) { ubody.sections = ids; ubody.sections_url = window.location.pathname; }
        step = post('cart/update.js', ubody).then(stamp);
      }

      /* Adds run after the update and one at a time — each appends a line, which
         would shift the positional array if it were still in flight. A failed add
         is normal (gift out of stock); report a change only when one landed. Each
         carries `sections` too, so the LAST landed write holds the final render. */
      return p.adds.reduce(function (chain, item) {
        return chain.then(function () {
          var abody = { items: [item] };
          if (ids) { abody.sections = ids; abody.sections_url = window.location.pathname; }
          return post('cart/add.js', abody).then(stamp);
        });
      }, step).then(function () {
        ghostClear();
        if (!done) return false;
        var rendered = 0;
        surfs.forEach(function (surf) {
          var html = lastSections && lastSections[surf.id];
          if (html) { surf.apply(html); rendered++; }
        });
        paint(); /* server render doesn't know qualification — reveal/hide here */
        /* 'applied' = every surface on screen is already up to date, so the
           caller and announce() have nothing left to render. */
        return (surfs.length && rendered === surfs.length) ? 'applied' : true;
      });
    }).catch(function () { ghostClear(); return false; });
  }

  /* Serialize syncs: two racing runs would each plan against a cart the other is
     about to change. run() re-reads the cart itself, so no snapshot is threaded
     through here. */
  function sync() {
    queue = queue.then(run, run);
    return queue;
  }

  /* When gwp changed the cart, announce it so BOTH cart surfaces reconcile:
     run() already self-rendered whichever surface was on screen (cart page via
     applySection, or the drawer), but the OTHER surface is a separate section
     and would otherwise stay stale until reopened. A 'gwp'-tagged cart:updated
     makes ysl-cart.js / ysl-cart-drawer.js background-refresh themselves; gwp
     ignores its own tag below, so this can't loop. */
  function announce(changed) {
    /* 'applied' means run() already rendered every surface in the DOM — firing
       here would only make them fetch a render they already have. */
    if (changed === true) document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'gwp' } }));
  }

  /* Fold gwp's reconcile INTO the caller's own mutation, so a quantity change
     and the gift it scales are one request instead of three (the caller's
     write, gwp's cart.js read, gwp's write — ~400ms each, measured; see
     docs/wiki/cart-page-build.md).

     @param cart - what the CALLER predicts the cart will be after its own
       change, shaped like /cart.js: { total_price, items: [{ key, variant_id,
       product_id, quantity, final_line_price, properties }] }. Built from the DOM, which was
       rendered from the last authoritative response.
     @returns {} when the gift needs no change, null otherwise (see below).
       Callers still merge a non-null object into their updates, keyed by
       line item key, should folding ever come back.

     Keys, never positions: the Ajax docs recommend them precisely because a
     cart can hold several lines of the same variant, and a positional array
     writes the wrong line the moment our view of the cart is stale. A key that
     no longer exists just fails to match — and gwp's own listener still runs
     afterwards and reconciles for real, so a wrong guess costs a round trip
     rather than a wrong cart.

     null when the plan needs a line that isn't there yet: add.js is the only
     endpoint that can set the _gwp property, so new gift lines keep the old
     path.

     ALSO null whenever the gift itself has to change (10/2026). A folded gift
     bump rides the caller's request, which must stay on the page fetch so the
     customer's own change is still reported — and then the pixel reports the
     gift as an add_to_cart too. Declining the fold leaves the bump to the sync
     that cart:updated always runs right after, which goes out quietly. Costs
     one round trip only on a ladder step (the gift line is otherwise dormant
     at its current qty and paint() toggles it instantly). */
  function fold(cart) {
    if (!cart || !cart.items) return null;
    var p = plan(cart);
    return p.changed ? null : {};
  }

  /* ---------- visibility ----------
     The gift line is ALWAYS in the cart now, so showing/hiding it on
     qualification is a pure CSS toggle — instant, no round trip. paint() reads
     the cart straight off the DOM so it can run optimistically (on a stepper tap,
     before any server render) and again after each render to stay in step. */
  function surfEls() {
    return [].slice.call(document.querySelectorAll('.ysl-cart[data-section-id], [data-cart-drawer]'));
  }
  function lineQty(el) {
    var inp = el.querySelector('[data-qty-input]');
    if (inp) return num(inp.value);
    var f = el.querySelector('.ysl-cart__qty-fixed, .ysl-cd__qty-fixed');
    var m = f && f.textContent.match(/(\d+)/);
    return m ? num(m[1]) : 1;
  }
  function domCart(surf) {
    var items = [], total = 0;
    surf.querySelectorAll('[data-cart-item]').forEach(function (el) {
      var q = lineQty(el), gid = el.getAttribute('data-gwp');
      items.push({
        variant_id: el.getAttribute('data-variant-id'),
        product_id: el.getAttribute('data-product-id'),
        quantity: q,
        final_line_price: num(el.getAttribute('data-unit')) * q,
        properties: gid ? { _gwp: gid } : {}
      });
      total += num(el.getAttribute('data-unit')) * q; /* gifts are 0₫ → contribute 0 */
    });
    /* data-unit is item.final_price — already net of LINE-level discounts, but an
       ORDER (cart-level) discount is not in it. Subtract it so a min_subtotal gift
       is judged on what the shopper actually pays, matching cart.total_price that
       the server plan uses. Attr is stale for one optimistic tap (the discount
       amount only updates on the next server render), then reconciles. */
    total -= num(surf.getAttribute('data-gwp-discount'));
    if (total < 0) total = 0;
    return { total_price: total, items: items };
  }
  function paint() {
    surfEls().forEach(function (surf) {
      var cart = domCart(surf);
      surf.querySelectorAll('[data-cart-item][data-gwp]').forEach(function (el) {
        var rule = ruleById(el.getAttribute('data-gwp'));
        /* No rule = orphan (its campaign was switched off). plan() removes it and
           the checkout handler would too, so hide it now instead of flashing a
           free gift for the ~400ms until the sync lands. */
        var earned = rule ? wanted(cart, rule) : 0;
        el.classList.toggle('is-gwp-off', earned <= 0);
      });
    });
  }

  /* ---------- checkout gate ----------
     A dormant (unearned) gift stays in the cart, hidden — free at 0₫, so it does
     no harm sitting there, but the customer must not walk away with a gift they
     didn't earn. Strip every unearned gift the moment they head to checkout, then
     let the click through. There is no express checkout on this store, so the
     main button (cart form submit / drawer link) is the only door.

     A login_only gift is earned on paper but only for a logged-in customer, so a
     guest loses it here. Waits for queued syncs first, or one still running could
     add the gift back after the strip. */
  var goingToCheckout = false;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="checkout"], [data-cd-checkout]');
    if (!btn || goingToCheckout) return;
    e.preventDefault();
    function readCart() { return fetch(routesRoot() + 'cart.js', { headers: { Accept: 'application/json' } }); }
    queue.then(readCart, readCart)
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var real = hasReal(cart), updates = {};
        cart.items.forEach(function (item) {
          if (!isGift(item)) return;
          var rule = ruleById(item.properties._gwp);
          var earned = rule ? wanted(cart, rule) : 0;
          if (!real || earned <= 0 || (rule && rule.login_only)) updates[item.key] = 0; /* unearned → drop it */
        });
        goingToCheckout = true;
        if (!Object.keys(updates).length) { btn.click(); return; }
        post('cart/update.js', { updates: updates }).then(function () { btn.click(); }, function () { btn.click(); });
      })
      .catch(function () { goingToCheckout = true; btn.click(); });
  });

  window.YSLGwp = { sync: sync, plan: plan, fold: fold, paint: paint, quietFetch: quietFetch };

  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.source === 'gwp') return; /* our own announce — don't re-run */
    paint(); /* optimistic: reveal/hide from the DOM NOW, before the async sync */
    sync().then(announce);
  });

  document.addEventListener('DOMContentLoaded', function () {
    paint();
    sync().then(announce);
  });

  /* Back from checkout can restore this page from memory with the flag still set,
     and the next click would then skip the gate. The strip above also took gifts
     out of a cart this page still shows, so sync again. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    goingToCheckout = false;
    sync().then(announce);
  });
})();
